import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { therapistsApi } from '@/api/endpoints';
import { SPECIALTIES } from '@/lib/specialties';
import { toSlug } from '@/lib/seo';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';

export const Route = createFileRoute('/especialidades/$slug')({ component: EspecialidadeDetailPage });

function EspecialidadeDetailPage() {
  const { slug } = Route.useParams();
  const spec = SPECIALTIES.find((s) => toSlug(s) === slug);

  const { data = [], isLoading } = useQuery({
    queryKey: ['therapists', { specialty: spec }],
    queryFn: () => therapistsApi.list({ specialty: spec }),
    enabled: !!spec,
  });

  if (!spec) {
    return (
      <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
        <PublicHeader />
        <main className="flex-1 max-w-3xl mx-auto px-6 py-16">
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-100 shadow-sm">
            <p className="text-slate-500">Especialidade não encontrada.</p>
            <Link to="/especialidades" className="inline-block mt-4 text-teal-600 hover:underline">
              ← Ver todas
            </Link>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-5xl mx-auto px-6 py-12 w-full">
        <div className="mb-8">
          <Link to="/especialidades" className="text-sm text-teal-600 hover:underline">
            ← Todas as especialidades
          </Link>
          <h1 className="text-4xl font-extrabold text-slate-800 mt-3">{spec}</h1>
          <p className="text-slate-500 mt-2">
            {isLoading ? 'Carregando...' : `${data.length} profissional${data.length === 1 ? '' : 'is'} especializado${data.length === 1 ? '' : 's'} em ${spec.toLowerCase()}.`}
          </p>
        </div>

        {!isLoading && data.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
            <p className="text-slate-500">Nenhum profissional cadastrado nesta especialidade ainda.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.map((t: any) => (
              <Link
                key={t.id}
                to="/terapeutas/$id"
                params={{ id: t.id }}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition group"
              >
                <div className="flex items-center gap-3">
                  {t.avatarUrl || t.user?.image ? (
                    <img src={t.avatarUrl ?? t.user.image} alt="" className="size-12 rounded-full object-cover" />
                  ) : (
                    <div className="size-12 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold">
                      {(t.user?.name ?? '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-700 truncate group-hover:text-teal-600">
                      {t.user?.name}
                    </p>
                    <p className="text-xs text-slate-400">R$ {Number(t.sessionPrice).toFixed(2)}/sessão</p>
                  </div>
                </div>
                {t.bio && <p className="mt-3 text-sm text-slate-600 line-clamp-3">{t.bio}</p>}
              </Link>
            ))}
          </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
